import { useCallback, useEffect, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { TooltipPlacement, computeTooltipPlacement } from './useTooltipPosition'

const DEFAULT_TOOLTIP_WIDTH = 390
const DEFAULT_TOOLTIP_HEIGHT = 200
const DEFAULT_VERTICAL_GAP = 10
const DEFAULT_EDGE_PADDING = 16

type Props = {
  map: maplibregl.Map
  latitude: number
  longitude: number
  tooltipWidth?: number
  tooltipHeight?: number
  verticalGap?: number
  edgePadding?: number
  verticalTop?: number
}

function getDefaultPlacement(): TooltipPlacement {
  return {
    position: 'top',
    horizontalOffset: 0,
    arrowOffset: 0,
    left: 0,
    top: 0,
    transform: 'translate(-50%, -100%)',
    isOutOfViewport: false,
  }
}

export function useMapLibreTooltipPosition({
  map,
  latitude,
  longitude,
  tooltipWidth = DEFAULT_TOOLTIP_WIDTH,
  tooltipHeight = DEFAULT_TOOLTIP_HEIGHT,
  verticalGap = DEFAULT_VERTICAL_GAP,
  edgePadding = DEFAULT_EDGE_PADDING,
  verticalTop = 60,
}: Props): TooltipPlacement {
  const [placement, setPlacement] =
    useState<TooltipPlacement>(getDefaultPlacement)

  const calculatePlacement = useCallback(() => {
    if (!map) return getDefaultPlacement()
    const screenPos = map.project([longitude, latitude])
    const container = map.getContainer()
    return computeTooltipPlacement(
      screenPos.x,
      screenPos.y,
      container.clientWidth,
      container.clientHeight,
      { tooltipWidth, tooltipHeight, verticalGap, edgePadding, verticalTop },
    )
  }, [
    map,
    latitude,
    longitude,
    tooltipWidth,
    tooltipHeight,
    verticalGap,
    edgePadding,
    verticalTop,
  ])

  useEffect(() => {
    setPlacement(calculatePlacement())
  }, [calculatePlacement])

  useEffect(() => {
    if (!map) return
    const update = () => setPlacement(calculatePlacement())
    map.on('move', update)
    map.on('moveend', update)
    return () => {
      map.off('move', update)
      map.off('moveend', update)
    }
  }, [map, calculatePlacement])

  return placement
}
